import { RowModel } from '@tanstack/react-table'
import { useCallback, useEffect, useState } from 'react'

import { IUser } from '@/common'
import { UserTableColumns } from '@/modules/user/columns'
import { useRoleAndPermission } from '@/providers'
import { useQueryGetOrganizations, useQueryGetUsers } from '@/services'

export const useUserTable = (orgId?: string) => {
  const [users, setUsers] = useState<IUser[]>([])
  const { isFetching, data, isFetched } = useQueryGetUsers()
  const {
    data: orgsQueryResp,
    isFetched: isOrgsFetched,
    isFetching: isOrgsFetching
  } = useQueryGetOrganizations()

  const {
    rolesMap,
    setGetAllRolesOrgIds,
    setGetAssignedRoleUsers,
    isReadyExport,
    userIdAssignedRoleIdsMap,
    loadingGetAllRolesOrgIds,
    loadingGetAssignedRoleUsers
  } = useRoleAndPermission()

  const onPaginationChange = useCallback(
    (rows: RowModel<IUser>) => {
      const paginatedUsers = rows.flatRows

      setGetAllRolesOrgIds(
        paginatedUsers.map((user) => user.original.organization_id ?? '')
      )
      setGetAssignedRoleUsers(paginatedUsers.map((user) => user.original))
    },
    [setGetAllRolesOrgIds, setGetAssignedRoleUsers]
  )

  useEffect(() => {
    if (
      isFetched &&
      isOrgsFetched &&
      !loadingGetAllRolesOrgIds &&
      !loadingGetAllRolesOrgIds
    ) {
      const tempUsers = (data as unknown as IUser[])?.map((user) => {
        const userAssignedRoleIds = userIdAssignedRoleIdsMap[user.id!] ?? []
        const rolesArray = userAssignedRoleIds.map((roleId) => {
          return rolesMap[roleId]
        })

        return {
          ...user,
          organization:
            orgsQueryResp?.find((org) => org?.id === user?.organization_id) ??
            null,
          roles: rolesArray
        }
      })

      if (orgId) {
        const orgUsers = tempUsers.filter(
          (user) => user.organization_id === orgId
        )

        setUsers(orgUsers as any)

        return
      }

      setUsers(tempUsers as any)
    }
  }, [
    data,
    isFetched,
    isOrgsFetched,
    orgId,
    orgsQueryResp,
    loadingGetAllRolesOrgIds,
    loadingGetAssignedRoleUsers,
    rolesMap,
    userIdAssignedRoleIdsMap
  ])

  return {
    data: users,
    isLoading: isFetching || isOrgsFetching,
    isRolesLoading: loadingGetAllRolesOrgIds || loadingGetAssignedRoleUsers,
    columns: UserTableColumns,
    isReadyExport,
    onPaginationChange
  }
}
